/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ContentApiService, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { DocumentListService } from '@alfresco/adf-content-services';
import { NodeEntry } from '@alfresco/js-api';
import { TranslatePipe } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MyTasksService, TaskRow } from '../workflow/my-tasks.service';
import { ExpiryDoc, ExpiryService } from '../expiry/expiry.service';
import { WorkflowSettingsService } from '../workflow/workflow-settings.service';
import { MenuPermissionsService } from '../menu-permissions/menu-permissions.service';

interface QuickAction {
  label: string;
  icon: string;
  route: string;
  /** Hidden while the administrator has workflow switched off. */
  workflow?: boolean;
  /** Only administrators may open it. */
  admin?: boolean;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, MatCardModule, MatIconModule, MatButtonModule, PageStateComponent, ThaiDatePipe, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  private readonly contentApi = inject(ContentApiService);
  private readonly documentList = inject(DocumentListService);
  private readonly myTasks = inject(MyTasksService);
  private readonly expiry = inject(ExpiryService);
  private readonly router = inject(Router);
  private readonly workflowSettings = inject(WorkflowSettingsService);
  private readonly menuPerms = inject(MenuPermissionsService);

  readonly loading = signal(true);
  readonly displayName = signal('');
  readonly recent = signal<NodeEntry[]>([]);
  readonly stats = signal({ total: 0, folders: 0, files: 0 });

  /** Open workflow tasks — the "งานของฉัน" widget under the quick actions. */
  readonly myWork = signal<TaskRow[]>([]);
  readonly myWorkTotal = signal(0);

  /**
   * Documents the reader can see whose expiry date is passed or coming. The
   * search answers only what the caller may read, so the widget works for
   * everyone — only the manage page behind "see all" is administrators'.
   */
  readonly expiring = signal<ExpiryDoc[]>([]);
  readonly expiringSoon = signal(0);
  readonly expired = signal(0);
  readonly isAdmin = signal(false);

  readonly skeletons = Array.from({ length: 5 });

  /**
   * The tiles point at this application's own screens.
   *
   * Workflow is a first-class citizen here: starting one, the user's inbox,
   * tracking, and the admin console all sit beside the document actions.
   */
  /** The hero's one call to action; the tiles below repeat the rest. */
  readonly heroAction: QuickAction = { label: 'DMS.DASHBOARD.HERO_CTA', icon: 'east', route: '/all-documents' };

  readonly quickActions: QuickAction[] = [
    { label: 'DMS.DASHBOARD.TILE_START_WF', icon: 'play_circle', route: '/workflows/start', workflow: true },
    { label: 'DMS.DASHBOARD.TILE_UPLOAD', icon: 'cloud_upload', route: '/upload' },
    { label: 'DMS.DASHBOARD.TILE_ALL', icon: 'description', route: '/all-documents' },
    { label: 'DMS.DASHBOARD.TILE_MYWORK', icon: 'inbox', route: '/my-work', workflow: true },
    { label: 'DMS.DASHBOARD.TILE_TRACK', icon: 'timeline', route: '/workflows/track', workflow: true },
    { label: 'DMS.DASHBOARD.TILE_MANAGE', icon: 'settings_suggest', route: '/settings/workflows', workflow: true, admin: true }
  ];

  /**
   * The tiles this person can actually open — the same rules the routes
   * enforce, so a tile never leads to a page that bounces them elsewhere.
   */
  readonly visibleActions = computed(() =>
    this.quickActions.filter(
      (action) =>
        (!action.workflow || this.workflowSettings.enabled()) &&
        (!action.admin || this.isAdmin()) &&
        this.menuPerms.isRouteAllowed(action.route.slice(1))
    )
  );

  ngOnInit(): void {
    this.contentApi.getPerson('-me-').subscribe({
      next: (person) => {
        this.displayName.set(person?.entry?.displayName || person?.entry?.firstName || '');
        this.isAdmin.set(!!person?.entry?.capabilities?.isAdmin);
      },
      error: () => this.displayName.set('')
    });

    this.loadCounts();
    this.loadRecent();
    this.loadMyWork();
    this.loadExpiring();
  }

  /**
   * The ones needing attention first — expired ahead of soon — five at most.
   * `listWithExpiry` answers nearest-expiry first, so the two lists split at
   * "expired" without another sort.
   */
  private loadExpiring(): void {
    this.expiry
      .listWithExpiry(200)
      .pipe(catchError(() => of([])))
      .subscribe((docs) => {
        const due = docs.filter((doc) => doc.status !== 'active');
        this.expired.set(due.filter((doc) => doc.status === 'expired').length);
        this.expiringSoon.set(due.filter((doc) => doc.status === 'soon').length);
        this.expiring.set(due.slice(0, 5));
      });
  }

  openDoc(doc: ExpiryDoc): void {
    this.router.navigate(['/document', doc.id]);
  }

  /** The five most pressing tasks — open ones and claimable pooled ones. */
  private loadMyWork(): void {
    forkJoin({ todo: this.myTasks.todo(), pooled: this.myTasks.pooled() }).subscribe(({ todo, pooled }) => {
      const mine = new Set(todo.map((t) => t.id));
      const all = [...todo, ...pooled.filter((p) => !mine.has(p.id))];
      this.myWorkTotal.set(all.length);
      this.myWork.set(all.slice(0, 5));
    });
  }

  openTask(row: TaskRow): void {
    if (row.id === row.processId) {
      void this.router.navigate(['/workflows/instances', row.processId]);
    } else {
      void this.router.navigate(['/my-work/task', row.id]);
    }
  }

  /**
   * Counts the contents of the user's own area.
   *
   * Each figure is the server's `totalItems` for a filtered query rather than a
   * tally of whatever came back in one page. The previous version asked for the
   * first fifty children and counted folders among them, so both figures were
   * simply wrong for any folder holding more than fifty things.
   */
  private loadCounts(): void {
    const count = (where: string) =>
      this.contentApi.getNodeChildren('-my-', { maxItems: 1, where }).pipe(
        map((paging) => paging?.list?.pagination?.totalItems ?? 0),
        catchError(() => of(0))
      );

    forkJoin({ folders: count('(isFolder=true)'), files: count('(isFile=true)') }).subscribe((counts) => {
      this.stats.set({ folders: counts.folders, files: counts.files, total: counts.folders + counts.files });
    });
  }

  /**
   * The same "recent" the เอกสารล่าสุด screen shows.
   *
   * Read through ADF's own service so both come from one query — content the
   * user modified or created in the last thirty days. The previous version
   * listed the most recently changed children of the home folder, which is a
   * different set, so "ดูทั้งหมด" led somewhere that did not contain them.
   */
  private loadRecent(): void {
    // the service answers with a DocumentLoaderNode — { currentNode, children } —
    // not a NodePaging, so the entries come from `children`
    this.documentList
      .loadFolderByNodeId('-recent-', { maxItems: 6, skipCount: 0 }, [])
      .pipe(catchError(() => of(null)))
      .subscribe((node) => {
        this.recent.set((node?.children?.list?.entries as NodeEntry[]) ?? []);
        this.loading.set(false);
      });
  }

  open(node: NodeEntry): void {
    const entry = node.entry;
    if (entry.isFolder) {
      this.router.navigate(['/repository', entry.id]);
    } else {
      this.router.navigate(['/document', entry.id]);
    }
  }

  iconFor(node: NodeEntry): string {
    return node.entry.isFolder ? 'folder' : 'description';
  }

  typeLabelFor(node: NodeEntry): string {
    return node.entry.isFolder ? 'DMS.DASHBOARD.TYPE_FOLDER' : 'DMS.DASHBOARD.TYPE_FILE';
  }

  goToAllDocuments(): void {
    this.router.navigate(['/all-documents']);
  }
}
